import { FC, useRef, useState, useEffect } from "react";
import { Search } from "lucide-react";
import { useDebounce } from "src/libs/hooks";
import { AxiosError } from "axios";
import useStore from "src/hooks/useStore/useStore";
import StoreSearchItem from "src/components/StoreSearchItem/StoreSearchItem";

interface Props {
	value?: string;
	onChange: (user: Partial<Store>) => void;
}

const StoreSearch: FC<Props> = ({ value, onChange }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [debouncedSearchValue, setDebouncedSearchValue] = useState<
		string | undefined
	>();

	const listInnerRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const {
		data,
		isError,
		error,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage
	} = useStore(debouncedSearchValue, {
		enabled: Boolean(debouncedSearchValue)
	});

	useDebounce(() => setDebouncedSearchValue(value), 300, [value]);

	useEffect(() => {
		if (debouncedSearchValue) {
			setIsOpen(true);
		}
	}, [debouncedSearchValue, data]);

	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, []);

	const handleScroll = () => {
		if (listInnerRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
			if (scrollTop + clientHeight === scrollHeight) {
				console.log("reached bottom");
				hasNextPage && fetchNextPage();
			}
		}
	};

	const stores = data ? data?.pages.flatMap(({ results }) => results) : [];

	if (isError && error)
		return <p className="text-red-600">{(error as AxiosError).message}</p>;

	return (
		<div className="rounded-lg px-6 py-4" ref={containerRef}>
			<div className="relative">
				<input
					type="text"
					placeholder="Begin typing store name"
					value={value}
					onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
						onChange({ name: target.value });
						if (target.value) setIsOpen(true);
					}}
					onFocus={() => debouncedSearchValue && setIsOpen(true)}
					className="w-full border-b-2 border-foreground bg-white shadow-xs py-2 pr-8 focus:outline-none"
					aria-label="Begin typing store name"
				/>

				<span className="absolute right-0 top-1/2 -translate-y-1/2">
					{isFetchingNextPage ? (
						<span className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin block" />
					) : (
						<Search size={24} />
					)}
				</span>
			</div>

			{isOpen && stores.length > 0 && (
				<div
					ref={listInnerRef}
					onScroll={handleScroll}
					aria-label="List of retail stores"
					className="absolute z-10 bg-white border border-border rounded-md shadow-lg max-h-60 overflow-y-auto w-auto min-w-[280px]"
				>
					{stores.map((store) => (
						<button
							key={`option-${store.id}`}
							type="button"
							className="w-full text-left hover:bg-muted"
							onClick={() => {
								onChange(store);
								setIsOpen(false);
							}}
						>
							<StoreSearchItem store={store} icon={null} />
						</button>
					))}
				</div>
			)}
		</div>
	);
};

export default StoreSearch;
